import React, { useEffect, createElement } from 'react';
import { ArrowRightIcon, ZapIcon, SmartphoneIcon, SearchIcon, ShieldCheckIcon, CalendarIcon, MonitorIcon, CodeIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteTipsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website tips | Verbeter je website | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Praktische website tips voor ondernemers. Verbeter je conversie, snelheid en vindbaarheid met deze bewezen tips. Door de webdesigners van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website tips | Verbeter je website | Noord',
      'og:description': 'Praktische website tips voor ondernemers. Verbeter je conversie, snelheid en vindbaarheid met deze bewezen tips.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-tips',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2943">
      {/* Hero */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2944">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2945">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2946" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2947" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2948" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2949">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2950">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2951">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2952">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2953">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2954">/</span>
            <span className="text-accent-light font-medium" data-id="element-2955">Website tips</span>
          </nav>
          <a href="#/kennisbank" className="inline-flex items-center gap-2 text-accent-light font-semibold text-sm tracking-widest uppercase mb-4 hover:text-white transition-colors animate-fade-in-up" data-id="element-2956">
            ← Kennisbank
          </a>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-2957">
            Website tips voor bedrijven
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto animate-fade-in-up-delay-2" data-id="element-2958">
            Praktische tips voor betere websites. Verbeter je online
            aanwezigheid en trek meer klanten aan.
          </p>
        </div>
      </section>

      {/* Tip 1 — Snelheid */}
      <section className="py-24 bg-surface-muted" data-id="element-2959">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2960">
          <div className="flex items-start gap-6" data-id="element-2961">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2962">
              <ZapIcon className="w-7 h-7 text-accent" data-id="element-2963" />
            </div>
            <div data-id="element-2964">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2965">
                Tip 1
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2966">
                Zorg dat je website snel laadt
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2967">
                Snelheid is een van de belangrijkste factoren voor een
                succesvolle website. Bezoekers verwachten dat een pagina binnen
                2 seconden laadt. Duurt het langer? Dan haakt meer dan 50% af
                voordat ze je content hebben gezien.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2968">
                Google gebruikt laadsnelheid als rankingfactor. Een snelle
                website scoort dus beter in de zoekresultaten. Optimaliseer je
                afbeeldingen, gebruik caching en kies voor betrouwbare hosting
                om je website snel te houden.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2969">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2970">
                  💡 Tip: Test je website snelheid gratis via Google PageSpeed
                  Insights en verbeter de punten die het meeste impact hebben.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tip 2 — Mobiel */}
      <section className="py-24 bg-white" data-id="element-2971">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2972">
          <div className="flex items-start gap-6" data-id="element-2973">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2974">
              <SmartphoneIcon className="w-7 h-7 text-accent" data-id="element-2975" />
            </div>
            <div data-id="element-2976">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2977">
                Tip 2
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2978">
                Maak je website mobielvriendelijk
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2979">
                Meer dan 60% van alle websitebezoeken komt tegenwoordig via een
                smartphone. Als je website niet goed werkt op mobiel, verlies je
                het grootste deel van je potentiële klanten.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2980">
                Een mobielvriendelijke website past zich automatisch aan het
                schermformaat aan. Knoppen zijn groot genoeg om op te tikken,
                tekst is leesbaar zonder te zoomen en formulieren zijn eenvoudig
                in te vullen op een klein scherm.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2981">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2982">
                  💡 Tip: Bekijk je eigen website op je telefoon. Kun je alles
                  makkelijk lezen en aanklikken? Zo niet, dan is het tijd voor
                  een update.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tip 3 — SEO */}
      <section className="py-24 bg-surface-muted" data-id="element-2983">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2984">
          <div className="flex items-start gap-6" data-id="element-2985">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2986">
              <SearchIcon className="w-7 h-7 text-accent" data-id="element-2987" />
            </div>
            <div data-id="element-2988">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2989">
                Tip 3
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2990">
                Investeer in SEO vanaf dag één
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2991">
                Zoekmachineoptimalisatie (SEO) is geen luxe, maar een noodzaak.
                Als je website niet vindbaar is in Google, missen potentiële
                klanten je bedrijf. SEO zorgt ervoor dat je structureel gevonden
                wordt door mensen die actief zoeken naar jouw diensten.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2992">
                Begin met de basis: gebruik relevante zoekwoorden in je
                paginatitels en teksten, schrijf unieke meta-beschrijvingen en
                zorg voor een logische sitestructuur. Technische SEO — zoals
                snelle laadtijden en een veilige verbinding (SSL) — is minstens
                zo belangrijk.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2993">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2994">
                  💡 Tip: Focus op lokale SEO als je een lokaal bedrijf hebt.
                  Vermeld je vestigingsplaats in titels en teksten, en claim je
                  Google Bedrijfsprofiel.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tip 4 — Vertrouwen */}
      <section className="py-24 bg-white" data-id="element-2995">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2996">
          <div className="flex items-start gap-6" data-id="element-2997">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2998">
              <ShieldCheckIcon className="w-7 h-7 text-accent" data-id="element-2999" />
            </div>
            <div data-id="element-3000">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-3001">
                Tip 4
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-3002">
                Bouw vertrouwen op met je website
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-3003">
                Bezoekers beslissen binnen enkele seconden of ze jouw website
                vertrouwen. Een professioneel design, duidelijke contactgegevens
                en sociale bewijskracht (reviews, logo's van klanten) maken het
                verschil.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-3004">
                Zorg voor een duidelijke over-ons pagina, toon je telefoonnummer
                en adres zichtbaar en gebruik echte foto's in plaats van
                stockfoto's. Een SSL-certificaat (het groene slotje) is
                tegenwoordig een absolute must voor het vertrouwen van bezoekers
                én Google.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-3005">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-3006">
                  💡 Tip: Vraag tevreden klanten om een review en plaats deze
                  zichtbaar op je website. Niets overtuigt beter dan de ervaring
                  van anderen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde artikelen */}
      <section className="py-24 bg-surface-muted" data-id="element-3007">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3008">
          <div className="text-center mb-12" data-id="element-3009">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-3010">
              Gerelateerde artikelen
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-3011">
            <a href="#/kosten-website-laten-maken" className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300" data-id="element-3012">
              <div className="aspect-[16/10] overflow-hidden" data-id="element-3013">
                <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop" alt="Kosten website laten maken" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-3014" />
              </div>
              <div className="p-6" data-id="element-3015">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2" data-id="element-3016">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-3017" />
                  15 feb 2026
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-accent transition-colors" data-id="element-3018">
                  Kosten website laten maken
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-3019">
                  Wat kost een professionele website in 2026? Wij zetten de
                  prijzen op een rij.
                </p>
                <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm mt-3 group-hover:gap-2.5 transition-all" data-id="element-3020">
                  Lees meer
                  <ArrowRightIcon className="w-4 h-4" data-id="element-3021" />
                </span>
              </div>
            </a>

            <a href="#/website-laten-maken-checklist" className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300" data-id="element-3022">
              <div className="aspect-[16/10] overflow-hidden" data-id="element-3023">
                <img src="https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=600&h=400&fit=crop" alt="Website laten maken checklist" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-3024" />
              </div>
              <div className="p-6" data-id="element-3025">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2" data-id="element-3026">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-3027" />8 feb 2026
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-accent transition-colors" data-id="element-3028">
                  Website laten maken checklist
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-3029">
                  Alles wat je moet weten voordat je een website laat maken.
                </p>
                <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm mt-3 group-hover:gap-2.5 transition-all" data-id="element-3030">
                  Lees meer
                  <ArrowRightIcon className="w-4 h-4" data-id="element-3031" />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde diensten */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-3032">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-3033" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3034">
          <div className="text-center mb-12" data-id="element-3035">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-3036">
              Gerelateerde diensten
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-3037">
            <a href="#/website-laten-maken" className="group bg-white/[0.06] border border-white/10 rounded-2xl p-8 hover:bg-white/[0.1] transition-all duration-300" data-id="element-3038">
              <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mb-5" data-id="element-3039">
                <MonitorIcon className="w-6 h-6 text-accent" data-id="element-3040" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-3041">
                Website laten maken
              </h3>
              <p className="text-navy-300 text-sm mb-4" data-id="element-3042">
                Professionele websites die klanten opleveren. Van strategie tot
                oplevering.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-3043">
                Bekijk dienst
                <ArrowRightIcon className="w-4 h-4" data-id="element-3044" />
              </span>
            </a>

            <a href="#/wordpress-websites" className="group bg-white/[0.06] border border-white/10 rounded-2xl p-8 hover:bg-white/[0.1] transition-all duration-300" data-id="element-3045">
              <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mb-5" data-id="element-3046">
                <CodeIcon className="w-6 h-6 text-accent" data-id="element-3047" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-3048">
                WordPress websites
              </h3>
              <p className="text-navy-300 text-sm mb-4" data-id="element-3049">
                Flexibele WordPress sites met onbeperkte mogelijkheden en
                eenvoudig beheer.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-3050">
                Bekijk dienst
                <ArrowRightIcon className="w-4 h-4" data-id="element-3051" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde links strip */}
      <section className="py-12 bg-white border-b border-navy-100" data-id="element-3052">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3053">
          <div className="flex flex-wrap justify-center gap-4" data-id="element-3054">
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3055">
              Homepage
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-3056">•</span>
            <a href="#/kennisbank" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-3057">
              Kennisbank
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-3058" />
    </main>;
}